// src/utils/productModel.js

// 🏷️ Product Types
export const PRODUCT_TYPES = {
  PRODUCT: 'product',
  RECIPE_BASED: 'recipe_based'
}

// 🎨 Product Type Configuration
export const PRODUCT_TYPE_CONFIG = {
  [PRODUCT_TYPES.PRODUCT]: {
    label: 'Product',
    value: 'product',
    icon: '📦',
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    description: 'Regular products sold directly'
  },
  [PRODUCT_TYPES.RECIPE_BASED]: {
    label: 'Recipe Based',
    value: 'recipe_based',
    icon: '🍳',
    color: 'orange',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-800',
    description: 'Products made from recipes'
  }
}

// 📋 Product Schema/Interface
export const ProductSchema = {
  _id: '',
  name: '',
  sku: '',
  category: '', // Category ID
  type: '',
  threshold: 0,
  unit: '', // Unit ID
  cost: 0,
  outletPrice: 0,
  pickMePrice: 0,
  uberPrice: 0,
  isActive: true,
  createdAt: null,
  updatedAt: null,
  // Populated fields (for display)
  categoryDetails: null,
  unitDetails: null
}

// ✅ Validation Rules
export const VALIDATION_RULES = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s\-&'().]+$/,
    message: 'Name must be 2-100 characters and contain only letters, numbers, spaces, and common symbols'
  },
  sku: {
    required: true,
    minLength: 2,
    maxLength: 20,
    pattern: /^[A-Z0-9\-]+$/,
    message: 'SKU must be 2-20 characters, uppercase letters, numbers, and hyphens only'
  },
  category: {
    required: true,
    message: 'Please select a category'
  },
  type: {
    required: true,
    enum: Object.values(PRODUCT_TYPES),
    message: 'Please select a valid product type'
  },
  unit: {
    required: true,
    message: 'Please select a unit'
  },
  threshold: {
    required: true,
    min: 0,
    max: 999999,
    message: 'Threshold must be between 0 and 999,999'
  },
  cost: {
    required: true,
    min: 0,
    max: 999999.99,
    message: 'Cost must be between 0 and 999,999.99'
  },
  outletPrice: {
    required: true,
    min: 0,
    max: 999999.99,
    message: 'Outlet price must be between 0 and 999,999.99'
  },
  pickMePrice: {
    required: true,
    min: 0,
    max: 999999.99,
    message: 'PickMe price must be between 0 and 999,999.99'
  },
  uberPrice: {
    required: true,
    min: 0,
    max: 999999.99,
    message: 'Uber price must be between 0 and 999,999.99'
  }
}

// 🔄 Product Model Utilities
export class ProductModel {
  // Validate product data
  static validateProduct(productData) {
    const errors = {}
    
    // Validate name
    if (!productData.name || !productData.name.trim()) {
      errors.name = 'Product name is required'
    } else if (productData.name.length < VALIDATION_RULES.name.minLength) {
      errors.name = `Name must be at least ${VALIDATION_RULES.name.minLength} characters`
    } else if (productData.name.length > VALIDATION_RULES.name.maxLength) {
      errors.name = `Name must be less than ${VALIDATION_RULES.name.maxLength} characters`
    } else if (!VALIDATION_RULES.name.pattern.test(productData.name)) {
      errors.name = VALIDATION_RULES.name.message
    }
    
    // Validate SKU
    if (!productData.sku || !productData.sku.trim()) {
      errors.sku = 'SKU is required'
    } else if (productData.sku.length < VALIDATION_RULES.sku.minLength) {
      errors.sku = `SKU must be at least ${VALIDATION_RULES.sku.minLength} characters`
    } else if (productData.sku.length > VALIDATION_RULES.sku.maxLength) {
      errors.sku = `SKU must be less than ${VALIDATION_RULES.sku.maxLength} characters`
    } else if (!VALIDATION_RULES.sku.pattern.test(productData.sku)) {
      errors.sku = VALIDATION_RULES.sku.message
    }
    
    // Validate category
    if (!productData.category) {
      errors.category = VALIDATION_RULES.category.message
    }
    
    // Validate type
    if (!productData.type) {
      errors.type = 'Product type is required'
    } else if (!VALIDATION_RULES.type.enum.includes(productData.type)) {
      errors.type = VALIDATION_RULES.type.message
    }
    
    // Validate unit
    if (!productData.unit) {
      errors.unit = VALIDATION_RULES.unit.message
    }
    
    // Validate threshold
    const threshold = parseFloat(productData.threshold)
    if (isNaN(threshold) || threshold < VALIDATION_RULES.threshold.min || threshold > VALIDATION_RULES.threshold.max) {
      errors.threshold = VALIDATION_RULES.threshold.message
    }
    
    // Validate prices
    const prices = ['cost', 'outletPrice', 'pickMePrice', 'uberPrice']
    prices.forEach(priceField => {
      const price = parseFloat(productData[priceField])
      if (isNaN(price) || price < VALIDATION_RULES[priceField].min || price > VALIDATION_RULES[priceField].max) {
        errors[priceField] = VALIDATION_RULES[priceField].message
      }
    })
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  // Create default product object
  static createDefaultProduct() {
    return {
      ...ProductSchema,
      _id: `temp_${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  }
  
  // Format product for display
  static formatProduct(product) {
    if (!product) return 'Unknown Product'
    return `${product.name} (${product.sku})`
  }
  
  // Format currency
  static formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }
  
  // Get product status badge classes
  static getStatusBadgeClasses(isActive) {
    return isActive
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-gray-100 text-gray-800 border-gray-200'
  }
  
  // Get product type badge classes
  static getTypeBadgeClasses(type) {
    const config = PRODUCT_TYPE_CONFIG[type]
    return config ? `${config.bgColor} ${config.textColor}` : 'bg-gray-100 text-gray-800'
  }
  
  // Get product type icon
  static getTypeIcon(type) {
    const config = PRODUCT_TYPE_CONFIG[type]
    return config ? config.icon : '📦'
  }
  
  // Transform product data for API
  static transformForAPI(productData) {
    return {
      name: productData.name?.trim(),
      sku: productData.sku?.trim().toUpperCase(),
      category: productData.category,
      type: productData.type,
      threshold: parseInt(productData.threshold),
      unit: productData.unit,
      cost: parseFloat(productData.cost),
      outletPrice: parseFloat(productData.outletPrice),
      pickMePrice: parseFloat(productData.pickMePrice),
      uberPrice: parseFloat(productData.uberPrice),
      isActive: productData.isActive !== false
    }
  }
  
  // Search products
  static searchProducts(products, searchTerm) {
    if (!searchTerm || !Array.isArray(products)) return products
    
    const lowercaseSearch = searchTerm.toLowerCase()
    return products.filter(product => {
      const name = product.name?.toLowerCase() || ''
      const sku = product.sku?.toLowerCase() || ''
      const categoryName = product.categoryDetails?.name?.toLowerCase() || ''
      
      return name.includes(lowercaseSearch) || 
             sku.includes(lowercaseSearch) || 
             categoryName.includes(lowercaseSearch)
    })
  }
  
  // Filter products by category
  static filterByCategory(products, categoryId) {
    if (!categoryId || !Array.isArray(products)) return products
    return products.filter(product => product.category === categoryId)
  }
  
  // Filter products by type
  static filterByType(products, type) {
    if (!type || !Array.isArray(products)) return products
    return products.filter(product => product.type === type)
  }
  
  // Filter products by status
  static filterByStatus(products, isActive) {
    if (typeof isActive !== 'boolean' || !Array.isArray(products)) return products
    return products.filter(product => product.isActive === isActive)
  }
  
  // Calculate profit margins
  static calculateProfitMargin(cost, sellingPrice) {
    if (!cost || !sellingPrice || cost <= 0) return 0
    return ((sellingPrice - cost) / cost) * 100
  }
  
  // Get all available product types for dropdown
  static getProductTypes() {
    return Object.values(PRODUCT_TYPE_CONFIG).map(config => ({
      value: config.value,
      label: config.label,
      icon: config.icon
    }))
  }
}

export default ProductModel